import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MultiEntitySelectionReportParameter } from '@ndt/report-output/shared/models/multi-entity-selection-report-parameter';
import { SelectedAccountReportParameter } from '@ndt/report-output/shared/models/selected-account-report-parameter';
import { createTimeRangeReportParameter } from '@ndt/report-output/shared/models/time-range-report-parameter-value';
import {
   convertFromFormValueList,
   ParameterFormValueUnion,
} from '@ndt/reports/shared/report-parameter-list/models/report-parameter-form-value';
import {
   BooleanReportParameter,
   convertToForm,
   EntitiesToIncludeParameter,
   ReportParametersUnion,
   TimeRangeParameter,
} from '@ndt/reports/shared/report-parameter-list/models/report-parameter-union.model';
import { ReportParameterConfig } from '@ndt/reports/shared/report-parameter-list/report-parameter.config';
import {
   ReportParameterFormService,
   ReportParameterFormServiceBase,
} from '@ndt/reports/shared/report-parameter-list/services/report-parameter-form.service';
import { ReportParameters } from 'generated/graphql-types';
import { LcsDateTimeAdapter } from 'src/app/common-components/date-time-picker/configuration/date-time-adapter';
import { throwIfNullOrUndefined } from 'src/app/common-components/utils/type-safety';
import { NdtWaitTimesReportParameters } from '../models/wait-times-tile';

export class NdtWaitTimesParametersFormService
   extends ReportParameterFormServiceBase
   implements ReportParameterFormService<NdtWaitTimesReportParameters>
{
   config: ReportParameterConfig[];

   form: UntypedFormGroup;

   accountId: number | null = null;

   constructor(fb: UntypedFormBuilder, private dateApdapter: LcsDateTimeAdapter<Date>) {
      super(fb);

      const initialValue: ReportParametersUnion[] = [
         {
            ...createTimeRangeReportParameter(),
            parameterType: ReportParameters.TimeRange,
            sortOrder: 1,
         },
         {
            parameterType: ReportParameters.SelectedAccount,
            sortOrder: 2,
            value: null,
            name: null,
         },
         {
            all: false,
            values: [],
            names: [''],
            parameterType: ReportParameters.CallQueueExtensionsToInclude,
            sortOrder: 3,
         },
         {
            parameterType: ReportParameters.AggregateQueues,
            sortOrder: 4,
            value: false,
            isAskParameter: false,
         },
      ];

      this.config = [
         {
            type: 'timeRange',
         },
         {
            type: 'selectedAccount',
         },
         {
            type: 'callQueueExtensions',
         },
         {
            label: 'Aggregate Queues',
            type: 'bool',
         },
      ];

      this.form = convertToForm(initialValue, fb);
      //this.contextArticleIds = [ContextHelpIds.ndtSettings];
   }

   convertValue(values: ReportParametersUnion[]): NdtWaitTimesReportParameters | null {
      const timeRange = values.find((v) => {
         return v.parameterType === ReportParameters.TimeRange;
      }) as TimeRangeParameter;

      const callQueues = values.find((v) => {
         return v.parameterType === ReportParameters.CallQueueExtensionsToInclude;
      }) as MultiEntitySelectionReportParameter;

      const selectedAccount = values.find((v) => {
         return v.parameterType === ReportParameters.SelectedAccount;
      }) as SelectedAccountReportParameter;
      const aggregateQueues = values.find((v) => {
         return v.parameterType === ReportParameters.AggregateQueues;
      }) as BooleanReportParameter;
      const output: any = {
         timeRange: throwIfNullOrUndefined({
            ...createTimeRangeReportParameter(timeRange),
            parameterType: ReportParameters.TimeRange,
         }),
         selectedAccount: selectedAccount,
         aggregateQueues: aggregateQueues,
         callQueues: throwIfNullOrUndefined(callQueues),
         tileLayoutConfiguration: this.getLayoutConfigurations(values),
      };
      return output;
   }

   getValue(): NdtWaitTimesReportParameters | null {
      const formValue = this.form.getRawValue();
      const values: ParameterFormValueUnion[] = this.convertNdtWaitTimesMembersAccountValue(formValue.values);
      const convertedValues = convertFromFormValueList(values);
      return this.convertValue(convertedValues);
   }

   isListComplete(values: ReportParametersUnion[]): boolean {
      const timeRange = values.find((v) => {
         return v.parameterType === ReportParameters.TimeRange;
      });
      const queues = values.find((v) => {
         return v.parameterType === ReportParameters.CallQueueExtensionsToInclude;
      }) as EntitiesToIncludeParameter;

      const selectedAccount = values.find((v) => {
         return v.parameterType === ReportParameters.SelectedAccount;
      }) as SelectedAccountReportParameter;

      return !!timeRange && !!queues && !!selectedAccount;
   }

   setValue(value: ParameterFormValueUnion[]): void {
      this.form.setValue(value);
   }

   patchSavedValues(values: ReportParametersUnion[]): void {
      super.patchSavedValues(values, false, false);
   }

   orderValues() {
      const { values } = this.form.value;
      this.form.setValue({
         values: [...values].map((v) => {
            return {
               ...v,
               sortOrder:
                  v.parameterType === ReportParameters.TimeRange
                     ? 1
                     : v.parameterType === ReportParameters.SelectedAccount
                     ? 2
                     : v.parameterType === ReportParameters.CallQueueExtensionsToInclude
                     ? 3
                     : v.parameterType === ReportParameters.AggregateQueues
                     ? 4
                     : v.sortOrder,
            };
         }),
      });
   }

   private convertNdtWaitTimesMembersAccountValue(values): ParameterFormValueUnion[] {
      return values.map((v) => {
         if (v.parameterType === ReportParameters.SelectedAccount) {
            if (v.value && v.value.accountId !== undefined) {
               this.accountId = v.value.accountId;
               return { ...v, value: v.value.accountId, name: v.value.name || 0 };
            } else {
               return { ...v, value: v.value || 0 };
            }
         }
         return v;
      });
   }
}
